// Guided insurance onboarding — the line-specific activation checklist.
// Purpose is narrower and more urgent than QuickstartPanel: this shows the
// hard gates between signup and "my line of business is actually running"
// (Life / ACA / Medicare each get their own launch sequence on top of the
// 4 common gates). Exec dashboard's north-star metric leans on the common
// 4; the line-specific steps are the deeper "insurance launch sequence."
//
// Renders steps DYNAMICALLY from /api/onboarding/activation's `steps`
// array (server decides which steps exist, in what order, from the
// subscriber's real insurance-line selection) — nothing is hardcoded here.
//
// The endpoint fails-soft: a broken DB check never crash-hides the widget,
// it just degrades that one step to not-done. This component mirrors that
// contract: silent while loading, silent once fully done.
//
// Dark-themed to the house palette (CRAFT.md §1) so it reads cleanly on
// the dark HQ backdrop the classic tabs now render on.

import React from 'react';
import { supabase } from '../lib/supabase';
import { normalizeLines } from './insuranceLines';

const apiFetch = async (path) => {
  const { data: { session } } = await supabase.auth.getSession();
  const jwt = session?.access_token;
  const res = await fetch(path, {
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
};

// Real signal the subscriber already chose during onboarding (Section 1 —
// see src/onboarding/insuranceLines.js). Reading it here (not the server)
// is the only place this data lives client-side; the endpoint falls back
// to the coarser workspaceMode when this is empty (new device, cleared
// storage, etc).
function readSelectedLines() {
  try {
    return normalizeLines(JSON.parse(localStorage.getItem('insurance_lines') || '[]'));
  } catch {
    return [];
  }
}

const palette = {
  card: 'rgba(20,23,30,0.82)',
  border: 'rgba(201,162,39,0.20)',
  borderStrong: 'rgba(201,162,39,0.45)',
  text: '#eef1f6',
  textSecondary: '#b7c0cc',
  gold: '#C9A227',
  goldBright: '#e8c65a',
  emerald: '#34d399',
  emeraldSoft: 'rgba(52,211,153,0.12)',
  goldSoft: 'rgba(201,162,39,0.12)',
  trackBg: 'rgba(255,255,255,0.06)',
};

const fontSerif = "'Fraunces', Georgia, serif";
const fontMono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const COLLAPSE_KEY = 'activation_checklist_expanded';

export default function ActivationChecklist({ onGotoTab }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  // Collapsed by default so the checklist never eats the fold on every tab —
  // the whole widget rides as a slim one-line bar until the user opts to
  // expand it. Preference is sticky across tabs/reloads.
  const [expanded, setExpanded] = React.useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const toggleExpanded = React.useCallback(() => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  const load = React.useCallback(async () => {
    try {
      const lines = readSelectedLines();
      const qs = lines.length ? `?lines=${encodeURIComponent(lines.join(','))}` : '';
      const r = await apiFetch(`/api/onboarding/activation${qs}`);
      setState({ loading: false, data: r, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: err.message || String(err) });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s while any step is incomplete so completing a
  // step elsewhere (Settings, an import, a signed consent) rewards the
  // user instantly without a manual reload.
  React.useEffect(() => {
    if (state.data?.all_done) return;
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [state.data?.all_done, load]);

  // Silent while loading (no flash of an empty widget), and silent once
  // fully done — the CRM should feel unbounded after activation. Also
  // silent if the endpoint returned no steps at all (fail-soft: an
  // empty/broken response is not an error banner, just nothing to show).
  if (state.loading || !state.data || state.data.all_done || !Array.isArray(state.data.steps) || state.data.steps.length === 0) {
    return null;
  }

  const { steps, completed_steps, total_steps, next_step_key, narration } = state.data;
  const pct = total_steps > 0 ? Math.round((completed_steps / total_steps) * 100) : 0;
  const nextStep = steps.find((s) => s.key === next_step_key) || steps.find((s) => !s.done) || null;

  const goto = (step) => {
    if (!step) return;
    // cta_tab is a real tab key the endpoint resolved against live nav —
    // falling back to window.location would break SPA session state, so
    // only do that if no cta_tab was provided.
    if (step.cta_tab && onGotoTab) {
      onGotoTab(step.cta_tab);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
      return;
    }
    if (step.cta_href) {
      window.location.href = step.cta_href;
    }
  };

  // Collapsed: a slim one-line bar. Shows progress + the single next action,
  // never covers the fold. One tap on the action jumps straight to the step;
  // the chevron expands the full sequence inline.
  if (!expanded) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: palette.card,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: `1px solid ${palette.border}`,
          borderRadius: 12,
          padding: '10px 14px',
          marginBottom: 16,
          color: palette.text,
        }}
      >
        <span
          style={{
            flexShrink: 0,
            fontFamily: fontMono,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: palette.gold,
          }}
        >
          Activate &middot; {completed_steps}/{total_steps}
        </span>
        <div
          style={{
            width: 90,
            height: 5,
            flexShrink: 0,
            background: palette.trackBg,
            borderRadius: 999,
            overflow: 'hidden',
            border: `1px solid ${palette.border}`,
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${palette.gold}, ${palette.emerald})`,
              transition: 'width 500ms ease',
            }}
          />
        </div>
        {nextStep && (
          <span
            style={{
              fontSize: 12.5,
              color: palette.textSecondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            Next: <span style={{ color: palette.text, fontWeight: 600 }}>{nextStep.label}</span>
          </span>
        )}
        {nextStep && (nextStep.cta_tab || nextStep.cta_href) && (
          <button
            onClick={() => goto(nextStep)}
            style={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              background: palette.gold,
              color: '#1a1200',
              cursor: 'pointer',
            }}
          >
            {nextStep.cta_label || 'Continue'} →
          </button>
        )}
        <button
          onClick={toggleExpanded}
          aria-label="Show all setup steps"
          style={{
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
            padding: '6px 10px',
            borderRadius: 8,
            border: `1px solid ${palette.border}`,
            background: 'transparent',
            color: palette.textSecondary,
            cursor: 'pointer',
          }}
        >
          Show all ▾
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: palette.card,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        border: `1px solid ${palette.border}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 16,
        color: palette.text,
        maxHeight: 'min(420px, 60vh)',
        overflowY: 'auto',
      }}
    >
      {narration && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 14,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          <span
            style={{
              flexShrink: 0,
              fontFamily: fontMono,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: palette.gold,
              background: palette.goldSoft,
              border: `1px solid ${palette.border}`,
              borderRadius: 999,
              padding: '3px 8px',
              marginTop: 1,
            }}
          >
            NOVA
          </span>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: palette.textSecondary }}>{narration}</p>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: fontMono,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: palette.gold,
            }}
          >
            Activate your account &middot; {completed_steps}/{total_steps}
          </div>
          <h3
            style={{
              fontFamily: fontSerif,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: palette.text,
              margin: '4px 0 0 0',
            }}
          >
            {completed_steps === 0
              ? 'Your insurance launch sequence starts here'
              : completed_steps === total_steps - 1
                ? 'Almost there, one more step'
                : `${total_steps - completed_steps} steps left to fully launch`}
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div
            style={{
              width: 112,
              height: 6,
              background: palette.trackBg,
              borderRadius: 999,
              overflow: 'hidden',
              border: `1px solid ${palette.border}`,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${palette.gold}, ${palette.emerald})`,
                transition: 'width 500ms ease',
              }}
            />
          </div>
          <span style={{ fontFamily: fontMono, fontSize: 11, fontWeight: 700, color: palette.textSecondary }}>{pct}%</span>
          <button
            onClick={toggleExpanded}
            aria-label="Collapse setup checklist"
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '5px 10px',
              borderRadius: 8,
              border: `1px solid ${palette.border}`,
              background: 'transparent',
              color: palette.textSecondary,
              cursor: 'pointer',
            }}
          >
            Collapse ▴
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {steps.map((step, idx) => {
          const done = !!step.done;
          const isNext = step.key === next_step_key;
          return (
            <div
              key={step.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 12,
                border: `1px solid ${done ? 'rgba(52,211,153,0.28)' : isNext ? palette.borderStrong : palette.border}`,
                background: done ? palette.emeraldSoft : isNext ? palette.goldSoft : 'rgba(255,255,255,0.02)',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: fontMono,
                  fontSize: 12,
                  fontWeight: 700,
                  background: done ? palette.emerald : isNext ? palette.gold : 'rgba(255,255,255,0.08)',
                  color: done || isNext ? '#0b0d12' : palette.textSecondary,
                }}
              >
                {done ? '✓' : idx + 1}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: done ? '#a7f3d0' : palette.text }}>
                  {step.label}
                </div>
                {step.detail && (
                  <div style={{ fontFamily: fontMono, fontSize: 10.5, marginTop: 2, color: done ? '#7fd6ae' : palette.textSecondary }}>
                    {step.detail}
                  </div>
                )}
                {!done && step.why && (
                  <div style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.4, color: palette.textSecondary }}>{step.why}</div>
                )}
              </div>
              {!done && (step.cta_tab || step.cta_href) && step.cta_label && (
                <button
                  onClick={() => goto(step)}
                  style={{
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: isNext ? 'none' : `1px solid ${palette.border}`,
                    background: isNext ? palette.gold : 'transparent',
                    color: isNext ? '#1a1200' : palette.textSecondary,
                    cursor: 'pointer',
                  }}
                >
                  {step.cta_label}
                </button>
              )}
              {done && (step.cta_tab || step.cta_href) && step.cta_label && (
                <button
                  onClick={() => goto(step)}
                  style={{
                    flexShrink: 0,
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: '5px 10px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: '#7fd6ae',
                    cursor: 'pointer',
                  }}
                >
                  {step.cta_label}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 10, fontStyle: 'italic', color: palette.textSecondary, opacity: 0.7, marginTop: 12 }}>
        Auto-refreshes every 30 seconds. This card disappears once every step is complete.
      </div>
    </div>
  );
}
